import type { SearchFilters, ArrayFilterItem } from '@/domain/entities/SearchFilters'
import type { AiSearchFilter } from '@/types/ai-search.types'
import type { AiSearchService } from '@/infrastructure/api/ai-search.service'
import { buildRangeFilter } from '@/utils/range-filter'

interface FieldMapping {
	target: keyof SearchFilters
	include: boolean
}

/**
 * Maps AI API response field names to SearchFilters targets.
 * Ported from Vue 2 AiFilter.vue includeExcludeFieldMap (lines 68-183).
 */
const AI_FIELD_MAP: Record<string, FieldMapping> = {
	industries_include: { target: 'Industry', include: true },
	industries_exclude: { target: 'Industry', include: false },
	business_country_include: { target: 'BusinessCountry', include: true },
	business_country_exclude: { target: 'BusinessCountry', include: false },
	world_region_include: { target: 'GlobalRegion', include: true },
	world_region_exclude: { target: 'GlobalRegion', include: false },
	company_website: { target: 'CompanyNameOrUrl', include: true },
	website: { target: 'CompanyNameOrUrl', include: true },
	company_name: { target: 'CompanyNameOrUrl', include: true },
	has_online_payments: { target: 'AnyOnlinePayments', include: true },
	turnover_min: { target: 'Turnover', include: true },
	turnover_max: { target: 'Turnover', include: true },
	contact_location_exclude: { target: 'ContactLocation', include: false },
	contact_location_include: { target: 'ContactLocation', include: true },
	contact_must_have_company_email: { target: 'MustHaveCompanyEmail', include: true },
	contact_must_have_direct_dial_mobile_phone: { target: 'MustHaveDirectDial', include: true },
	contact_must_have_employee_email_address: { target: 'MustHaveEmail', include: true },
	contact_must_have_phone_number: { target: 'MustHavePhoneNumber', include: true },
	contact_name: { target: 'ContactNameOrEmail', include: true },
	contact_title: { target: 'ContactTitle', include: true },
	contact_department_include: { target: 'ContactDepartment', include: true },
	contact_department_exclude: { target: 'ContactDepartment', include: false },
	contact_level_include: { target: 'ContactLevel', include: true },
	contact_level_exclude: { target: 'ContactLevel', include: false },
	contact_email_status: { target: 'ContactEmailStatus', include: true },
	localized_country_include: { target: 'LocalizedCountry', include: true },
	localized_country_exclude: { target: 'LocalizedCountry', include: false },
	sic_codes: { target: 'SIC', include: true },
	naic_codes: { target: 'NAIC', include: true },
	company_search_keyword: { target: 'Keywords', include: true },
	contains_in_url: { target: 'URLContains', include: true },
	location_city_state: { target: 'City', include: true },
	location_zip_postal: { target: 'ZipCode', include: true },
	has_add_to_cart: { target: 'HasAddToCart', include: true },
	has_b2c_evidence: { target: 'EvidenceOfB2C', include: true },
	has_b2b_evidence: { target: 'EvidenceOfB2B', include: true },
	has_international_domain: { target: 'IncludeLocalizedDomains', include: true },
	has_parent_only: { target: 'IncludeOnlyParent', include: true },
	has_localized_domain: { target: 'IncludeLocalizedDomains', include: true },
	has_purchased_leads: { target: 'IncludePurchasedLeads', include: true },
	has_purchased_contacts: { target: 'IncludePurchasedContacts', include: true },
	has_ignored_leads: { target: 'IncludeIgnoredLeads', include: true },
	has_ignored_contacts: { target: 'IncludeIgnoredContacts', include: true },
	has_physical_location: { target: 'HasPhysicalLocation', include: true },
	has_international_office: { target: 'HasInternationalOffices', include: true },
	has_own_brands: { target: 'HasOwnBrands', include: true },
	has_brand_reseller: { target: 'HasBrandReseller', include: true },
	has_all_sites_shipping: { target: 'AnyShippingIndicator', include: true },
	has_international_shipping: { target: 'InternationalShipping', include: true },
	has_online_presence_only: { target: 'OnlyOnlinePresence', include: true },
	has_marketplace_seller: { target: 'IsMarketSeller', include: true },
	has_multi_psp: { target: 'HasMultiPSP', include: true },
	number_of_employees_min: { target: 'NumberOfEmployees', include: true },
	number_of_employees_max: { target: 'NumberOfEmployees', include: true },
	facebook_likes_min: { target: 'FacebookLikes', include: true },
	facebook_likes_max: { target: 'FacebookLikes', include: true },
	facebook_followers_min: { target: 'FacebookFollowers', include: true },
	facebook_followers_max: { target: 'FacebookFollowers', include: true },
	twitter_followers_min: { target: 'TwitterFollowers', include: true },
	twitter_followers_max: { target: 'TwitterFollowers', include: true },
	instagram_followers_min: { target: 'InstagramFollowers', include: true },
	instagram_followers_max: { target: 'InstagramFollowers', include: true },
	youtube_views_min: { target: 'YoutubeViews', include: true },
	youtube_views_max: { target: 'YoutubeViews', include: true },
	youtube_subscribers_min: { target: 'YoutubeSubscribers', include: true },
	youtube_subscribers_max: { target: 'YoutubeSubscribers', include: true },
	age_of_website_min: { target: 'AgeOfSite', include: true },
	age_of_website_max: { target: 'AgeOfSite', include: true },
	website_rank_min: { target: 'SiteRank', include: true },
	website_rank_max: { target: 'SiteRank', include: true },
	global_website_traffic_min: { target: 'GlobalTraffic', include: true },
	global_website_traffic_max: { target: 'GlobalTraffic', include: true },
	company_estimated_sales_min: { target: 'EstimatedSales', include: true },
	company_estimated_sales_max: { target: 'EstimatedSales', include: true },
	import_export_volume_min: { target: 'ImporterExporterInfoVolume', include: true },
	import_export_volume_max: { target: 'ImporterExporterInfoVolume', include: true },
	import_export_frequency_months_min: { target: 'ImporterExporterInfoFrequency', include: true },
	import_export_frequency_months_max: { target: 'ImporterExporterInfoFrequency', include: true },
	website_top_level_domain_include: { target: 'TopLevelDomains', include: true },
	website_top_level_domain_exclude: { target: 'TopLevelDomains', include: false },
	visitor_traffic_country_include: { target: 'VisitorTrafficCountry', include: true },
	visitor_traffic_country_exclude: { target: 'VisitorTrafficCountry', include: false },
	visitor_traffic_region_include: { target: 'VisitorTrafficRegion', include: true },
	visitor_traffic_region_exclude: { target: 'VisitorTrafficRegion', include: false },
	importer_exporter_include: { target: 'ImportersExporters', include: true },
	importer_exporter_exclude: { target: 'ImportersExporters', include: false },
	shipping_to_country_include: { target: 'ShippingCountry', include: true },
	shipping_to_country_exclude: { target: 'ShippingCountry', include: false },
	tech_website_include: { target: 'Technologies', include: true },
	tech_website_exclude: { target: 'Technologies', include: false },
	tech_marketing_include: { target: 'Technologies', include: true },
	tech_marketing_exclude: { target: 'Technologies', include: false },
	tech_chatbot_include: { target: 'Technologies', include: true },
	tech_chatbot_exclude: { target: 'Technologies', include: false },
	shipping_include: { target: 'ShippingCompetitorInsights', include: true },
	shipping_exclude: { target: 'ShippingCompetitorInsights', include: false },
	payment_method_include: { target: 'PaymentMethods', include: true },
	payment_method_exclude: { target: 'PaymentMethods', include: false },
	payment_provider_include: { target: 'PaymentProviders', include: true },
	payment_provider_exclude: { target: 'PaymentProviders', include: false },
	finance_provider_include: { target: 'FinanceProviders', include: true },
	finance_provider_exclude: { target: 'FinanceProviders', include: false },
	hosting_provider_include: { target: 'HostingProviders', include: true },
	hosting_provider_exclude: { target: 'HostingProviders', include: false },
	sub_industries_include: { target: 'SubIndustry', include: true },
	sub_industries_exclude: { target: 'SubIndustry', include: false },
	company_number_include: { target: 'CompanyNumbers', include: true },
	company_number_exclude: { target: 'CompanyNumbers', include: false },
	vat_number_include: { target: 'VatNumbers', include: true },
	vat_number_exclude: { target: 'VatNumbers', include: false },
}

const BOOLEAN_TARGETS = new Set<keyof SearchFilters>([
	'AnyOnlinePayments',
	'HasAddToCart',
	'EvidenceOfB2C',
	'EvidenceOfB2B',
	'IncludeLocalizedDomains',
	'IncludeOnlyParent',
	'IncludePurchasedLeads',
	'IncludePurchasedContacts',
	'IncludeIgnoredLeads',
	'IncludeIgnoredContacts',
	'HasPhysicalLocation',
	'HasInternationalOffices',
	'HasOwnBrands',
	'HasBrandReseller',
	'AnyShippingIndicator',
	'InternationalShipping',
	'OnlyOnlinePresence',
	'IsMarketSeller',
	'HasMultiPSP',
	'MustHaveCompanyEmail',
	'MustHaveDirectDial',
	'MustHaveEmail',
	'MustHavePhoneNumber',
])

const RANGE_SUFFIX_RE = /_(min|max)$/

// The AI payload is untyped (`unknown` per field): a boolean can arrive as a real boolean, but
// also as the STRING "false"/"true" or 0/1. `Boolean("false")` is `true`, which silently flipped
// flags like IncludeOnlyParent ("Show parent only") on when the AI asked for them off — e.g.
// "Shopify stores in Ireland" came back with has_parent_only=false but rendered as true. Parse the
// value for what it means instead.
function toBool(value: unknown): boolean {
	if (typeof value === 'boolean') return value
	if (typeof value === 'number') return value !== 0
	if (typeof value === 'string') {
		const v = value.trim().toLowerCase()
		return !(v === '' || v === 'false' || v === '0' || v === 'no' || v === 'off')
	}
	return Boolean(value)
}

/**
 * Filters the backend matches on the ISO country code rather than the numeric country id,
 * so an AI suggestion's `code` is used in place of its `id`.
 *
 * LocalizedCountry and VisitorTrafficCountry are a deliberate DIVERGENCE from Vue 2, whose
 * `targetsUsingCode` (AiFilter.vue:289) lists only the first two and therefore sends the
 * id — which matches nothing server-side, the same way the sidebar dropdowns did before
 * they were keyed by code.
 */
const TARGETS_USING_CODE = new Set([
	'ContactLocation',
	'ShippingCountry',
	'LocalizedCountry',
	'VisitorTrafficCountry',
])

/**
 * Whether the AI mapped any part of the query to a filter (Vue 2 AiFilter: `groupedFilters` is
 * not empty). When it did not, Vue 2 applies nothing and the search on screen stays as it was.
 * Skips the same empty values as mapAiFiltersToSearchFilters.
 */
export function aiFiltersUnderstood(aiFilter: AiSearchFilter): boolean {
	return Object.entries(aiFilter).some(
		([key, value]) =>
			key in AI_FIELD_MAP &&
			value != null &&
			value !== '' &&
			!(Array.isArray(value) && value.length === 0),
	)
}

/**
 * Maps AI API response fields to a partial SearchFilters patch.
 * The caller applies via filtersStore.loadFromSavedSearch(patch).
 */
export async function mapAiFiltersToSearchFilters(
	aiFilter: AiSearchFilter,
	aiSearchService: AiSearchService,
): Promise<Record<string, unknown>> {
	const patch: Record<string, unknown> = {}

	for (const [key, value] of Object.entries(aiFilter)) {
		// Skip empty/null/unknown values
		if (value == null) continue
		if (value === '') continue
		if (Array.isArray(value) && value.length === 0) continue

		const mapping = AI_FIELD_MAP[key]
		if (!mapping) continue

		const { target, include } = mapping

		// Boolean targets
		if (BOOLEAN_TARGETS.has(target)) {
			patch[target] = toBool(value)
			continue
		}

		// Range fields (_min / _max suffix)
		if (RANGE_SUFFIX_RE.test(key)) {
			if (!patch[target]) {
				patch[target] = {
					IsModified: true,
					IsMinModified: false,
					IsMaxModified: false,
					Range: [0, 0],
				}
			}
			const range = patch[target] as {
				IsMinModified: boolean
				IsMaxModified: boolean
				Range: number[]
			}
			if (key.endsWith('_min')) {
				range.IsMinModified = true
				range.Range[0] = Number(value)
			} else {
				range.IsMaxModified = true
				range.Range[1] = Number(value)
			}
			continue
		}

		// Array fields — append to Items (multiple AI keys can map to the same target)
		if (!patch[target]) {
			patch[target] = { Items: [] }
		}
		const arrayFilter = patch[target] as { Items: ArrayFilterItem[] }

		const items = Array.isArray(value) ? value : [value]
		const isCompanyName = key === 'company_name'

		for (const item of items) {
			let name: string
			let filterValue: string

			if (isCompanyName) {
				// Special case: enrich company name via suggestions API
				const raw = typeof item === 'object' ? (item?.name ?? item?.value ?? '') : String(item)
				let suggestion: string | null = null
				try {
					suggestion = await aiSearchService.getCompanySuggestion(String(raw))
				} catch {
					// Fall back to raw value
				}
				const resolved = suggestion || String(raw)
				name = String(raw)
				filterValue = resolved
			} else if (typeof item === 'object' && item !== null && 'name' in item && 'id' in item) {
				// Object with name/id (e.g., { name: "United Kingdom", id: "44", code: "GB" })
				name = item.name
				filterValue = TARGETS_USING_CODE.has(target) && item.code ? item.code : item.id
			} else if (target === 'Industry') {
				// Industry uses string ID as value
				name = String(item)
				filterValue = String(item)
			} else {
				name = String(item)
				filterValue =
					TARGETS_USING_CODE.has(target) &&
					typeof item === 'object' &&
					item !== null &&
					'code' in item
						? item.code
						: item
			}

			arrayFilter.Items.push({
				Name: name,
				Value: filterValue,
				Include: include,
			})
		}
	}

	// Vue 2 applies an AI result through updateFilterAndRunSearch, which resets every filter the AI
	// did not mention — and resetSelectedFilter sets booleans to FALSE, not to their default. Our
	// callers merge the patch over createDefaultFilters(), where IncludeOnlyParent defaults to true,
	// so a query that never mentions it ("Shopify stores in Ireland") would show parent-only results
	// that Vue 2 does not. It is the only default-true boolean, so pin it explicitly.
	if (!('IncludeOnlyParent' in patch)) {
		patch.IncludeOnlyParent = false
	}

	// Normalize range targets through the canonical builder. A bound the AI left unset stays 0 in
	// Range, and the backend derives Min from Range.Min() — so a min-only range like "turnover from
	// $5B" ([5B, 0]) would otherwise collapse to "turnover >= 0" (see range-filter.ts).
	for (const [target, entry] of Object.entries(patch)) {
		if (entry && typeof entry === 'object' && Array.isArray((entry as { Range?: unknown }).Range)) {
			const range = entry as { Range: number[]; IsMinModified: boolean; IsMaxModified: boolean }
			const min = range.IsMinModified ? range.Range[0] || 0 : 0
			const max = range.IsMaxModified ? range.Range[1] || 0 : 0
			patch[target] = buildRangeFilter(min, max)
		}
	}

	return patch
}
